import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router";

import { GlobalTopBar } from "@/components/shell/GlobalTopBar";
import { PhoneBottomBar } from "@/components/shell/PhoneBottomBar";
import { PhoneTopBar } from "@/components/shell/PhoneTopBar";
import { RouteAnnouncer } from "@/components/shell/RouteAnnouncer";
import { SearchOverlay } from "@/components/ui/SearchOverlay";
import { Toaster } from "@/components/ui/Toast";

/**
 * The layout route every destination renders inside (`main.tsx`'s router
 * table). Desktop: a sticky floating nav bar (`GlobalTopBar.tsx`, Story
 * 17.1) at the top of the content column's own scroll area. Phone: top bar
 * and bottom bar around the content, with bottom padding so the fixed
 * bottom bar never covers it.
 *
 * `ReadOnlyNotice` is not rendered here — it lives inside each destination's
 * own content column (`DestinationPlaceholder`), in the same centered
 * `max-w-record-column-max` wrapper as the title, so it sits next to the
 * content it's about rather than floating in the shell at a different
 * horizontal position (review round 1 finding: it previously rendered here,
 * misaligned 132px from the title it sat above).
 *
 * Story 9.2 — the search overlay's own open state and the global `/`/⌘K
 * keyboard listener both live here, the one place that already renders both
 * `GlobalTopBar` and `PhoneTopBar` as siblings (spec Intent), so wiring is
 * symmetric across every screen without touching route-level code. Both
 * triggers and the shortcut call the same `openSearch` — one open path, not
 * two or three (spec judgement call 5).
 *
 * `min-w-0` on the content column (Story 2.2 review finding): a flex item's
 * default `min-width` is `auto`, not `0` — meaning it refuses to shrink
 * below its content's min-content width. Story 2.1's placeholders never had
 * content wide enough for this to matter; Story 2.2's data table does (a
 * real `<table>` has a real min-content width), so between 768px and
 * ~857px the former 232px rail plus the table's min-content width exceeded
 * the viewport and the whole page scrolled horizontally — exactly what
 * UX-DR20's "usable at 200% zoom without horizontal scrolling" forbids.
 * `min-w-0` lets this column shrink to the viewport's actual width; the
 * table itself (`components/table/DataTable.tsx`) scrolls internally,
 * inside its own rounded shell, if a future column set is ever genuinely
 * too wide for the space — the page itself never does.
 *
 * Story 17.1 — the content column is now the scrolling container itself on
 * desktop (`md:h-screen md:overflow-y-auto`), not the document/`body`: the
 * floating bar needs a real scrolling ancestor to stick inside, and
 * `md:overscroll-contain` on that same container is the fix for the exact
 * bug found live during this story's own mockup exploration — without it,
 * scrolling past a short screen's own content end bubbles into the page
 * behind the bar and the "floating" bar scrolls away with it. `md:flex-none`
 * is load-bearing, not decorative (found live, not by inspection): the
 * unprefixed `flex-1` below (`flex-grow: 1; flex-basis: 0%`) is what this
 * column needs on phone, where it is not a flex item at all (`AppShell`'s
 * own outer wrapper is only `md:flex`) — but at `md:` that same `flex-1`
 * makes `flex-basis: 0%` win over the explicit `md:h-screen`, so the column
 * silently grows to its own content height instead of being capped to the
 * viewport, and `overflow-y: auto` then has nothing to ever scroll.
 * `md:flex-none` resets `flex-basis` to `auto` at that breakpoint, which is
 * what makes the explicit height apply at all. Phone itself is otherwise
 * untouched: `md:h-screen`/`md:flex-none`/`md:overflow-y-auto`/
 * `md:overscroll-contain` are all `md:`-prefixed, so below 768px this
 * column stays in normal document flow exactly as before, and the
 * document/`body` keeps scrolling the whole page the way `PhoneTopBar`/
 * `PhoneBottomBar` already assume.
 */
/** Whether `target` is somewhere a visitor is already typing — the global
 * `/` shortcut must not steal focus from a field they're already in (AC:
 * "`/` pressed while focus is on a page's own text input" -> nothing
 * happens), while ⌘K/Ctrl+K overrides regardless (spec judgement call 5).
 *
 * Found live: `NavRail`'s own search box was (at the time) a `readOnly`
 * `Input` (judgement call 4) — after focus returns to it (e.g. right after
 * selecting a result), it counted as a "typing target" under a bare
 * tag-name check, silently swallowing the very shortcut meant to reopen
 * search from that exact spot (a real user only had a click or ⌘K left in
 * that one state). A `readOnly` input can never actually receive typed
 * text, so it was never genuinely "somewhere a visitor is already typing"
 * in the first place — excluding it here is a correctness fix, not a
 * search-box-specific carve-out. The `readOnly`-input clause below is kept
 * exactly as it was for that reason, even though Story 15.18 later reshaped
 * `NavRail`'s trigger into a `<button>` (a visual-only change to that one
 * component, per that story's own spec) — a `<button>` already isn't
 * `INPUT`/`TEXTAREA`/content-editable, so it was never a typing target
 * either way, by the plain tag check alone. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target instanceof HTMLInputElement && target.readOnly) {
    return false;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

export function AppShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  // Found live: closing the overlay returns focus to whichever trigger
  // opened it (`Dialog`/`Sheet`'s own `useReturnFocusOnOpen`/
  // `focusReturnElement` convention, mirrored by `SearchOverlay.tsx`). When
  // that trigger is `NavRail`'s own search box/chip (Story 15.18 changed its
  // element, not this wiring) — `onFocus` also opening the overlay (spec
  // judgement call 4) — the programmatic
  // `.focus()` call closing produces fires that same `onFocus` handler and
  // reopens the overlay it was just closing, before the visitor ever sees
  // it close. This ref suppresses exactly that one spurious reopen attempt,
  // bracketed synchronously around `SearchOverlay`'s own return-focus call
  // via `onFocusReturn` below — not a timer guess (one was tried first and
  // lost a real race against Radix's own deferred focus scheduling).
  const suppressNextOpenRef = useRef(false);

  function openSearch() {
    if (suppressNextOpenRef.current) {
      suppressNextOpenRef.current = false;
      return;
    }
    setSearchOpen(true);
  }

  function returnFocusToTrigger(restoreFocus: () => void) {
    suppressNextOpenRef.current = true;
    restoreFocus();
    suppressNextOpenRef.current = false;
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isMetaK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (isMetaK) {
        // Overrides any browser/OS default for this shortcut (e.g.
        // Chrome's own address-bar-focus binding) regardless of where
        // focus currently is.
        event.preventDefault();
        openSearch();
        return;
      }
      if (event.key === "/" && !isTypingTarget(event.target)) {
        event.preventDefault();
        openSearch();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-surface-sunken md:flex md:flex-col">
      <PhoneTopBar onOpenSearch={openSearch} />
      {/* Story 17.1 — `GlobalTopBar` (the floating nav bar) moved from a
       * full-width sibling above the content column to a sticky child
       * *inside* it: "a floating bar at the top of the content area," not a
       * page-wide header above a sidebar. This column is the bar's own
       * scrolling container now (`md:h-screen md:overflow-y-auto
       * md:overscroll-contain` — see this component's own doc comment for
       * why), so the bar has to render inside it to stick against it. */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col bg-surface-sunken md:h-screen md:flex-none md:overflow-y-auto md:overscroll-contain">
        <RouteAnnouncer />
        <GlobalTopBar onOpenSearch={openSearch} />
        <main className="min-w-0 flex-1 bg-canvas px-4 py-6 pb-24 md:px-8 md:py-8 md:pb-8">
          <Outlet />
        </main>
        <PhoneBottomBar />
      </div>
      {/* Mounted once, above every route's own content — a toast survives
       * whatever navigation the action that triggered it causes next
       * (Story 2.6, DESIGN.md § Components `toast`). */}
      <Toaster />
      <SearchOverlay open={searchOpen} onOpenChange={setSearchOpen} onFocusReturn={returnFocusToTrigger} />
    </div>
  );
}
